import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Clock, LayoutGrid, Cpu, HeartHandshake, Network, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';

// Slider horizontal premium de beneficios — comunica cómo Punto Cero Salud
// mejora la práctica diaria de los profesionales (distinto al Hero).
const SLIDES = [
  {
    id: 'tiempo',
    icon: Clock,
    title: 'Más tiempo para cuidar',
    message: 'Menos tareas administrativas, más tiempo dedicado a tus pacientes.',
    image: 'https://images.unsplash.com/photo-1765896387387-0538bc9f997e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
    alt: 'Profesional de enfermería acompañando a un paciente',
    benefits: ['Menos tareas administrativas', 'Flujos automatizados', 'Más foco en el paciente'],
    color: '#14b8a6',
  },
  {
    id: 'organizada',
    icon: LayoutGrid,
    title: 'Tu práctica organizada',
    message: 'Agenda, pacientes y documentos conectados en un solo espacio.',
    image: 'https://images.unsplash.com/photo-1675270690434-aa99f4871e8a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
    alt: 'Profesional de salud usando tecnología en su consultorio',
    benefits: ['Agenda unificada', 'Pacientes centralizados', 'Documentos conectados'],
    color: '#06b6d4',
  },
  {
    id: 'tecnologia',
    icon: Cpu,
    title: 'Tecnología al servicio de la salud',
    message: 'Una herramienta creada para profesionales modernos.',
    image: 'https://images.unsplash.com/photo-1758691463582-11aea602cd4a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
    alt: 'Médico utilizando una plataforma digital',
    benefits: ['Interfaz moderna', 'Acceso desde cualquier lugar', 'Seguridad de datos'],
    color: '#3b82f6',
  },
  {
    id: 'humana',
    icon: HeartHandshake,
    title: 'Atención más humana',
    message: 'La tecnología ayuda a mejorar la experiencia de atención.',
    image: 'https://images.unsplash.com/photo-1758273240360-76b908e7582a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
    alt: 'Profesional conversando con su paciente',
    benefits: ['Comunicación cercana', 'Seguimiento continuo', 'Mejor experiencia'],
    color: '#8b5cf6',
  },
  {
    id: 'conectadas',
    icon: Network,
    title: 'Profesionales y clínicas conectadas',
    message: 'Gestiona equipos, pacientes y procesos desde una plataforma empresarial.',
    image: 'https://images.unsplash.com/photo-1623854767648-e7bb8009f0db?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
    alt: 'Doctora profesional sonriendo con estetoscopio',
    benefits: ['Multiusuario', 'Gestión de equipos', 'Procesos empresariales'],
    color: '#10b981',
  },
];

const AUTO_MS = 6000;

export default function SaludBenefitsSlider() {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  const go = useCallback((d) => {
    setDir(d);
    setIndex((i) => (i + d + SLIDES.length) % SLIDES.length);
  }, []);

  const jump = (i) => {
    setDir(i > index ? 1 : -1);
    setIndex(i);
  };

  useEffect(() => {
    if (paused) return undefined;
    timer.current = setTimeout(() => { setDir(1); setIndex((i) => (i + 1) % SLIDES.length); }, AUTO_MS);
    return () => clearTimeout(timer.current);
  }, [index, paused]);

  const slide = SLIDES[index];
  const Icon = slide.icon;

  return (
    <section id="beneficios-experiencia" data-testid="salud-benefits-slider" className="relative py-20 px-6 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0b1220] via-[#0f172a] to-[#0b1220]" />
      <motion.div aria-hidden="true" animate={{ opacity: [0.2, 0.4, 0.2] }} transition={{ repeat: Infinity, duration: 10, ease: 'easeInOut' }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[34rem] h-[34rem] bg-[#14b8a6]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto relative z-10 max-w-5xl">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-10">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 text-[#14b8a6] text-xs font-bold uppercase tracking-[0.25em] mb-5">
            <Sparkles className="w-4 h-4" /> Tu práctica, mejorada
          </span>
          <h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight tracking-tight">
            Cómo Punto Cero Salud <span className="bg-gradient-to-r from-[#14b8a6] via-[#06b6d4] to-[#10b981] bg-clip-text text-transparent">transforma tu día a día</span>
          </h2>
        </motion.div>

        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="relative overflow-hidden rounded-[2rem] border border-white/12 bg-white/[0.05] backdrop-blur-2xl shadow-[0_40px_120px_-40px_rgba(0,0,0,0.85)] min-h-[24rem]">
            <AnimatePresence mode="wait" custom={dir}>
              <motion.div
                key={slide.id}
                custom={dir}
                initial={{ opacity: 0, x: dir > 0 ? 80 : -80 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: dir > 0 ? -80 : 80 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className="grid md:grid-cols-2"
              >
                {/* Imagen */}
                <div className="relative h-56 md:h-auto md:min-h-[24rem] overflow-hidden">
                  <img src={slide.image} alt={slide.alt} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0" style={{ background: `linear-gradient(120deg, ${slide.color}22, rgba(15,23,42,0.35))` }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/80 via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:to-[#0f172a]/70" />
                </div>

                {/* Contenido */}
                <div className="relative p-8 md:p-11 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center border" style={{ background: `${slide.color}1f`, borderColor: `${slide.color}55` }}>
                      <Icon className="w-6 h-6" style={{ color: slide.color }} />
                    </div>
                    <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: slide.color }}>Beneficio {String(index + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-extrabold text-white leading-tight mb-3">{slide.title}</h3>
                  <p className="text-white/70 text-base md:text-lg leading-relaxed mb-6 font-light">{slide.message}</p>
                  <ul className="space-y-2.5">
                    {slide.benefits.map((b) => (
                      <li key={b} className="flex items-center gap-2.5 text-white/85">
                        <CheckCircle className="w-5 h-5 shrink-0" style={{ color: slide.color }} /> {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Controles */}
          <div className="flex items-center justify-center gap-4 mt-7">
            <button aria-label="Anterior" data-testid="benefit-prev" onClick={() => go(-1)} className="w-11 h-11 rounded-full border border-white/15 bg-white/5 flex items-center justify-center text-white/80 hover:bg-white/10 hover:text-white transition-all">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              {SLIDES.map((s, i) => (
                <button
                  key={s.id}
                  aria-label={`Ir al beneficio ${i + 1}`}
                  data-testid={`benefit-dot-${i}`}
                  onClick={() => jump(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${i === index ? 'w-8 bg-gradient-to-r from-[#14b8a6] to-[#06b6d4]' : 'w-2 bg-white/25 hover:bg-white/40'}`}
                />
              ))}
            </div>
            <button aria-label="Siguiente" data-testid="benefit-next" onClick={() => go(1)} className="w-11 h-11 rounded-full border border-white/15 bg-white/5 flex items-center justify-center text-white/80 hover:bg-white/10 hover:text-white transition-all">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
